import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal, notification } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import _ from 'lodash';
import uuid from 'react-uuid';
import {
  addRecords,
  deleteRecords,
  updateRecords,
} from "auth/actions/objActions";
import { trackActivity } from "services/helper/activity";
import sleep from "components/Utils/sleep";

const { confirm } = Modal;

const normalizeValue = (value) => {
  if (value === undefined || value === null) return null;

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (trimmed === "0") return null;
    if (trimmed.toLowerCase() === "nan") return null;
    return trimmed;
  }

  if (typeof value === "number" && value === 0) return null;

  return value;
};

const normalizeMultiValue = (value) => {
  if (!value) return [];

  // vindo do form (array de objetos)
  if (Array.isArray(value)) {
    return value
      .map(v => v?.value)
      .filter(v => v !== null && v !== undefined && v !== '')
      .map(String)
      .sort();
  }

  // vindo do banco (string "a, b, c")
  if (typeof value === 'string') {
    return value
      .split(',')
      .map(v => v.trim())
      .filter(Boolean)
      .sort();
  }

  return [String(value)];
};

const checkIfIsEmpty = (data) => {
  return data && _.every(data, (item) => _.isEmpty(item.id) && (_.isNil(item.value) || (!_.isNumber(item.value) && _.isEmpty(item.value))))
}

const useRecordOperations = ({
  name,
  user,
  checkedRows,
  setOpenForm,
  fields, // props.formData.fields
  setCheckedRowsData,
  refreshTable
}) => {
  const dispatch = useDispatch();
  const [loadingSend, setLoadingSend] = useState(false);
  const currentForm = decodeURIComponent(name);

  const handleDelete = () => {
    var ids = checkedRows.map((values) => values._id) || [];
    confirm({
      title: "Deseja excluir esses registros?",
      icon: <ExclamationCircleOutlined />,
      content: "Se confirmado e necessário, acesse a área de arquivamento dentro deste formulário para recuperar os registros.",
      onOk() {
        const actionDate = new Date().toISOString();
        return deleteRecords({
          formName: name,
          ids,
          useBin: false,
          isObjDataDelete: true,
          createdBy: user.sub,
        })
          .then(async (res) => {
            notification['success']({
              message: res.message
            })
            const changes = [];

            checkedRows.forEach((row) => {
              Object.keys(row).forEach((field) => {
                // ignora campo _id
                if (['_id'].includes(field)) return;

                const oldField = row[field];
                const oldValue = Array.isArray(oldField)
                  ? oldField[0]?.value ?? null
                  : oldField ?? null;

                changes.push({
                  id: row._id,
                  field,
                  value: null,
                  oldValue,
                });
              });
            });

            if (changes.length > 0) {
              const auditPayload = {
                form: { name: name.slice(4) },
                action: "DELETE",
                device: "web",
                page: "Records",
                network: "online",
                ids,
                changes,
                rawData: checkedRows,
                isReverted: false,
                createdBy: user.sub,
                createdAt: actionDate,
              };

              trackActivity(auditPayload);
            }

            refreshTable();
          })
          .catch((err) => notification['error']({
            message: "Algo está errado, tente novamente!"
          }));
      },
    });
  };

  const handleSave = (data, action) => {
    if (loadingSend) return;


    // Extrai os IDs dos registros selecionados
    var ids = checkedRows.map((values) => values._id);
    // Combina resultados buscando campos de retorno com nomes "duplicados"
    const keys = Object.keys(data);
    const uniqueKeys = new Set(keys);
    const haveDuplicateField = keys.length !== uniqueKeys.size;

    if (data && !_.isEmpty(data)) {
      if (haveDuplicateField) {
        notification['error']({
          message: "Não é permitido anexar campos de retornos com nomes duplicados. Por favor, remova os campos com nome duplicados dos campos de consulta."
        })
      } else {
        const actionDate = new Date().toISOString();
        if (action && action === "add") {
          return addRecords({
            formName: currentForm,
            record: data,
            createdBy: user.sub,
          })
            .then(async (res) => {
              notification["success"]({
                message: res.message
              });
              const newRecordId = res.data[0];
              const changes = Object.keys(data).map((field) => {
                const entry = Array.isArray(data[field]) ? data[field][0] : null;
                return {
                  id: newRecordId,
                  field,
                  value: entry?.value || null,
                  oldValue: null
                };
              });
              const finalRecord = {};
              Object.keys(data).forEach((field) => {
                const entry = Array.isArray(data[field]) ? data[field][0] : data[field];
                finalRecord[field] = entry?.value ?? null;
              });

              const auditPayload = {
                form: { name: currentForm.slice(4) },
                action: "CREATE",
                device: "web",
                page: "Records",
                network: "online",
                ids: [newRecordId],
                changes,
                rawData: [finalRecord],
                isReverted: false,
                createdBy: user.sub,
                createdAt: actionDate
              };
              trackActivity(auditPayload);
              await sleep(500);
              refreshTable();
              setOpenForm(false);
              return {
                success: true,
                type: 'field',
                fields: []
              }
            })
            .catch((err) => {
              const errorResponse = err?.response?.data;
              if (errorResponse?.message?.fields) {
                return {
                  success: false,
                  type: 'field',
                  fields: errorResponse.message.fields,
                  error: errorResponse.message.error
                }
              }
              notification['error']({
                message: "Algo está errado, tente novamente!"
              })
            });
        }

        else if (action && action === "edit") {
          return updateRecords({
            formName: currentForm,
            ids,
            records: data,
            forRemove: [],
            createdBy: user.sub,
          })
            .then(async (res) => {
              notification['success']({
                message: res.message,
              });

              // assumindo edição de 1 registro por vez
              const originalRow = checkedRows[0];

              const changes = Object.keys(data)
                .map((field) => {

                  const newRaw = data[field];
                  const oldRaw = originalRow[field];

                  const isMulti = Array.isArray(newRaw);

                  const newValue = isMulti
                    ? normalizeMultiValue(newRaw)
                    : normalizeValue(newRaw?.[0]?.value ?? newRaw);

                  const oldValue = isMulti
                    ? normalizeMultiValue(oldRaw)
                    : normalizeValue(Array.isArray(oldRaw) ? oldRaw[0]?.value : oldRaw);

                  const isEqual = isMulti
                    ? JSON.stringify(newValue) === JSON.stringify(oldValue)
                    : newValue === oldValue;

                  if (isEqual) return null;

                  return {
                    id: originalRow._id,
                    field,
                    value: isMulti ? newValue.join(', ') : newValue,
                    oldValue: isMulti ? oldValue.join(', ') : oldValue,
                  };
                })
                .filter(Boolean);
              const finalRecord = { ...originalRow };

              Object.keys(data).forEach((field) => {
                const raw = data[field];

                if (Array.isArray(raw)) {
                  finalRecord[field] = normalizeMultiValue(raw).join(', ');
                } else {
                  const newValue = normalizeValue(raw?.value ?? raw);
                  if (newValue !== null) {
                    finalRecord[field] = newValue;
                  }
                }
              });

              if (changes.length > 0) {
                const auditPayload = {
                  form: { name: currentForm.slice(4) },
                  action: "UPDATE",
                  device: "web",
                  page: "Records",
                  network: "online",
                  ids,
                  changes,
                  rawData: [finalRecord],
                  isReverted: false,
                  createdBy: user.sub,
                  createdAt: actionDate,
                };
                trackActivity(auditPayload);
              }
              await sleep(500);
              refreshTable();
              setOpenForm(false);
              return {
                success: true,
                type: 'field',
                fields: []
              }
            })
            .catch((err) => {
              const errorResponse = err?.response?.data;
              if (errorResponse?.message?.fields) {
                return {
                  success: false,
                  type: 'field',
                  fields: errorResponse.message.fields,
                  error: errorResponse.message.error
                }
              }

              notification['error']({
                message: "Algo está errado, tente novamente!"
              });
            });
        }

        else if (action && action === "multipleEdit") {
          const requiredFieldsList = fields.filter(item => !!item.requiredField).map(item => item.field);
          const emptyFields = Object.entries(data).filter(([key, value]) => checkIfIsEmpty(value)).map(([key]) => key)
          const fieldsByKey = _.keyBy(fields, 'field');

          let intersection = _.intersection(requiredFieldsList, emptyFields);
          for (let i = 0; i < requiredFieldsList.length; i++) {
            const field = fieldsByKey[requiredFieldsList[i]]
            if (field.fieldtype === "refLookup" && _.includes(intersection, field.field)) {
              for (let j = 0; j < field.getValueOf.length; j++) {
                const item = field.getValueOf[j];
                intersection = [...intersection, ...[item.fieldName]];
              }
            }
          }
          // Lógica de filtro de campos vazios
          if (!data.acceptEmptyValues) {
            data = _.omit(data, ['acceptEmptyValues', ...emptyFields]);
          } else {
            data = _.omit(data, intersection);
          }

          return updateRecords({
            formName: currentForm,
            ids,
            records: data,
            forRemove: [],
            createdBy: user.sub,
          })
            .then(async (res) => {
              notification['success']({
                message: res.message
              });

              const changes = [];

              checkedRows.forEach((row) => {
                Object.keys(data).forEach((field) => {
                  if (field === 'acceptEmptyValues') return;
                  const rawNew = data[field];
                  const rawOld = row[field];
                  const isMulti = Array.isArray(rawNew);
                  const newValue = isMulti
                    ? normalizeMultiValue(rawNew)
                    : normalizeValue(rawNew?.[0]?.value ?? rawNew);
                  const oldValue = isMulti
                    ? normalizeMultiValue(rawOld)
                    : normalizeValue(Array.isArray(rawOld) ? rawOld[0]?.value : rawOld);

                  // Se o valor novo é nulo e não é multi, ignora (a menos que a intenção seja limpar, mas o log só deve mostrar mudança real)
                  if (newValue === null && !isMulti && oldValue === null) return;

                  const isEqual = isMulti
                    ? JSON.stringify(newValue) === JSON.stringify(oldValue)
                    : String(newValue) === String(oldValue);

                  if (!isEqual) {
                    changes.push({
                      id: row._id,
                      field,
                      value: isMulti ? newValue.join(', ') : newValue,
                      oldValue: isMulti ? oldValue.join(', ') : oldValue,
                    });
                  }
                });
              });

              if (changes.length > 0) {
                const finalRecords = checkedRows.map((row) => {
                  const updatedRow = { ...row };
                  Object.keys(data).forEach((field) => {
                    const raw = data[field];

                    if (Array.isArray(raw)) {
                      updatedRow[field] = normalizeMultiValue(raw).join(', ');
                    } else {
                      const newValue = normalizeValue(raw?.value ?? raw);
                      if (newValue !== null) {
                        updatedRow[field] = newValue;
                      }
                    }
                  });
                  return updatedRow;
                });

                const auditPayload = {
                  form: { name: currentForm.slice(4) },
                  action: "UPDATE",
                  device: "web",
                  page: "Records",
                  network: "online",
                  ids,
                  changes,
                  rawData: finalRecords,
                  isReverted: false,
                  createdBy: user.sub,
                  createdAt: actionDate,
                };

                trackActivity(auditPayload);
              }
              await sleep(1000);
              refreshTable();
              setOpenForm(false);
              return {
                success: true,
                type: 'field',
                fields: []
              }
            })
            .catch((err) => {
              const errorResponse = err?.response?.data;
              if (errorResponse?.message?.fields) {
                return {
                  success: false,
                  type: 'field',
                  fields: errorResponse.message.fields,
                  error: errorResponse.message.error
                }
              }

              notification['error']({
                message: "Algo está errado, tente novamente!"
              })
            }
            );
        }
      }
    } else {
      notification['error']({
        message: "Você precisa preencher algum dos campos para confirmar o envio."
      })
    }
  };

  return {
    handleDelete,
    handleSave,
    loadingSend,
    setLoadingSend,
    checkIfIsEmpty
  };
};

export default useRecordOperations;
